export const ChampionPickStatus = {
	NotPicked: "NOT_PICKED",
	LockedIn: "LOCKED_IN",
	Champion: "CHAMPION",
	Missed: "MISSED",
	Eliminated: "ELIMINATED",
};

/** The champion's team id once the final is decided, else null. */
export function resolveChampionTeamId(format, eventData, officialBracketPicks) {
	const championPick = format?.championPick;
	if (!championPick?.enabled) {
		return null;
	}

	const finalStageId =
		eventData?.eventStages?.[championPick.finalStageIndex]?.id;
	if (!finalStageId) {
		return null;
	}

	const finalMatch = (officialBracketPicks ?? []).find(
		(p) =>
			p.eventStageId === finalStageId &&
			`${p.bracketPart}` === `${championPick.finalPart}` &&
			p.bracketCol === Number(championPick.finalCol) &&
			p.bracketColIndex === 0 &&
			p.winnerTeamId
	);

	return finalMatch?.winnerTeamId ?? null;
}

/** Post-start pick status. */
export function getChampionPickStatus({
	format,
	eventData,
	officialBracketPicks,
	officialGroupPicks,
	championPick,
}) {
	if (!championPick?.teamId) {
		return ChampionPickStatus.NotPicked;
	}

	const championTeamId = resolveChampionTeamId(
		format,
		eventData,
		officialBracketPicks
	);
	if (championTeamId) {
		return championTeamId === championPick.teamId
			? ChampionPickStatus.Champion
			: ChampionPickStatus.Missed;
	}

	if (
		isEliminated(
			format,
			eventData,
			officialBracketPicks,
			officialGroupPicks,
			championPick.teamId
		)
	) {
		return ChampionPickStatus.Eliminated;
	}

	return ChampionPickStatus.LockedIn;
}

/** True when the loser of this match drops into another match. */
function matchHasLoserPath(stage, pick) {
	const parts = stage?.bracket?.freeformParts?.length
		? stage.bracket.freeformParts
		: stage?.bracket?.parts;
	const part = (parts ?? []).find(
		(p) => `${p.identifier}` === `${pick.bracketPart}`
	);
	const match = part?.columns
		?.flatMap((c) => c.matches ?? [])
		.find(
			(m) =>
				m.col === pick.bracketCol && m.colIndex === pick.bracketColIndex
		);

	return match ? !!match.loserPath : true;
}

function isEliminated(
	format,
	eventData,
	officialBracketPicks,
	officialGroupPicks,
	teamId
) {
	const finalStageIndex = Number(format?.championPick?.finalStageIndex);
	const finalStage = format?.stages?.[finalStageIndex];
	const finalStageId = eventData?.eventStages?.[finalStageIndex]?.id;

	// A loss only eliminates when there's no loser path.
	const lostInFinalStage = (officialBracketPicks ?? []).some(
		(p) =>
			p.eventStageId === finalStageId &&
			p.winnerTeamId &&
			p.loserTeamId === teamId &&
			!matchHasLoserPath(finalStage, p)
	);
	if (lostInFinalStage) {
		return true;
	}

	const advancingPickType = 1;
	for (let stage = 0; stage < (eventData?.stage ?? 0); stage++) {
		const stageId = eventData?.eventStages?.[stage]?.id;
		const playedStage = (eventData?.teams ?? []).some(
			(t) => t.id === teamId && t.eventStageId === stageId
		);
		if (!playedStage) {
			continue;
		}

		const stageFormat = format?.stages?.[stage];
		const expectedAdvancing =
			Number(stageFormat?.groupStageDetails?.totalAdvancing) ||
			Number(stageFormat?.swissStageDetails?.advancingCount) ||
			0;
		const advancing = (officialGroupPicks ?? []).filter(
			(p) =>
				p.eventStageId === stageId && p.pickTypeId === advancingPickType
		);
		if (
			expectedAdvancing > 0 &&
			advancing.length >= expectedAdvancing &&
			!advancing.some((p) => p.teamId === teamId)
		) {
			return true;
		}
	}

	return false;
}
